import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Home, Flag, Table, Plus, Radio } from "lucide-react";
import { useProtocolShell } from "@/contexts/ProtocolShellContext";
import { getNativePlatform } from "@/lib/runtime";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import NativeBottomNav from "@/components/native/NativeBottomNav";

export const bottomNavItems = [
  { id: "home", to: "/", label: "Hem", icon: Home },
  { id: "live", to: "/live", label: "Live", icon: Radio },
  { id: "new", to: "/matches", label: "Ny", icon: Plus, isCenter: true },
  { id: "table", to: "/tabell", label: "Tabell", icon: Table },
  { id: "series", to: "/serie", label: "Serie", icon: Flag },
];

export default function BottomNav() {
  const location = useLocation();
  const { openShell, openShellMenu, resumeProtocolSession, clearActiveProtocol, hasActiveProtocol, isWidgetDragging } = useProtocolShell();
  const [resumePromptOpen, setResumePromptOpen] = React.useState(false);
  const useNativeV3 = getNativePlatform() === "android";

  const handleCenterClick = React.useCallback(() => {
    if (useNativeV3) {
      openShellMenu();
      return;
    }
    if (hasActiveProtocol) {
      setResumePromptOpen(true);
      return;
    }
    openShell();
  }, [hasActiveProtocol, openShell, openShellMenu, useNativeV3]);

  return (
    <>
      {useNativeV3 ? (
        <NativeBottomNav
            items={bottomNavItems}
            isHidden={isWidgetDragging}
            onCreate={handleCenterClick}
        />
      ) : <nav
        className={[
          "fixed bottom-0 inset-x-0 z-50 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 lg:hidden transition-[transform,opacity] duration-300",
          isWidgetDragging ? "translate-y-full opacity-0 pointer-events-none" : "translate-y-0 opacity-100",
        ].join(" ")}
      >
        <ul className="native-bottom-nav-content relative mt-2 grid grid-cols-5">
          {bottomNavItems.map(({ id, to, label, icon: Icon, isCenter, disabled }) => (
            <li key={id} className={`flex flex-col items-center text-xs ${isCenter ? "relative -mt-4" : ""}`}>
              {isCenter ? (
                <button
                  data-tutorial-target="nav-new"
                  onClick={handleCenterClick}
                  className="bg-primary text-white p-3 rounded-full shadow-xl hover:bg-[#a11e25] focus:outline-none"
                >
                  <Icon size={24} />
                </button>
              ) : disabled ? (
                <div className="flex flex-col items-center text-muted">
                  <Icon size={20} />
                  <span>{label}</span>
                </div>
              ) : (
                <NavLink
                  data-tutorial-target={
                    id === "home"
                      ? "nav-home"
                      : id === "live"
                        ? "nav-live"
                        : id === "table"
                          ? "nav-table"
                          : id === "series"
                            ? "nav-series"
                            : undefined
                  }
                  to={to}
                  className={({ isActive }) => {
                    const active = isActive || (to === "/account/matches" && location.pathname.startsWith("/account/matches"));
                    return `flex flex-col items-center ${active ? "text-primary" : "text-muted"}`;
                  }}
                >
                  <Icon size={20} />
                  <span>{label}</span>
                </NavLink>
              )}
            </li>
          ))}
        </ul>
      </nav>}

      <AlertDialog open={resumePromptOpen} onOpenChange={setResumePromptOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Du har ett pågående protokoll</AlertDialogTitle>
            <AlertDialogDescription>
              Vill du återuppta det pågående protokollet eller starta om från matchkort?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              className="bg-secondary text-secondary-foreground hover:bg-secondary/80"
              onClick={() => {
                setResumePromptOpen(false);
                clearActiveProtocol();
                openShell();
              }}
            >
              Starta nytt
            </AlertDialogAction>
            <AlertDialogAction
              className="sm:ml-0"
              onClick={() => {
                setResumePromptOpen(false);
                resumeProtocolSession();
              }}
            >
              Återuppta
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

